import {
  Component,
  ComponentInterface,
  Event,
  EventEmitter,
  Fragment,
  getMode,
  h,
  Prop,
  State,
  Watch
} from '@stencil/core';
import { Labels, translate } from '../../../../utils/translations';
import { ActivityMapRegion, emitClickTrackerEvent } from '../../../../utils/utils';
import {
  CLASS_NAME_VERTICAL_SCROLLBAR,
  CLASS_NAME_VISIBLE_SCROLLBAR
} from '../../../ds-secondary-navigation/common/constants';

export interface CountrySelectorConfig {
  enabled: boolean;
  searchDisabled?: boolean | number;
  regions: Region[] | (() => Promise<Region[]>);
  defaultActiveCountry?: {
    countryCode: string;
    iconUrl?: string;
  };
}

export interface Country {
  countryCode: string;
  displayTitle: string;
  nativeTitle?: string;
  url?: string;
}

export interface Language {
  title: string;
  url?: string;
}

export interface Region {
  title: string;
  countries: Country[];
}

@Component({
  tag: 'ds-contextual-navigation-country-selector',
  styleUrls: {
    default: 'ds-contextual-navigation-country-selector.scss',
    light: 'ds-contextual-navigation-country-selector.scss'
  },
  shadow: true
})
export class ContextualNavigationCountrySelector implements ComponentInterface {
  private _dropdown!: HTMLElement;

  @Prop() regions: Region[] | (() => Promise<Region[]>);
  /** (optional) is displayed in the bottom of dropdown */
  @Prop() lastVisitedCountry?: Country;
  /** (optional) if boolean, disables/enabled search field for countries
   * If number: search will be disabled if amount of countries smaller or equals then provided value */
  @Prop() searchDisabled?: boolean | string | number = false;

  @Prop() defaultActiveCountry?: CountrySelectorConfig['defaultActiveCountry'];

  @Prop({ reflect: true }) open: boolean = false;
  @Prop() mobile?: boolean = false;
  @State() activeRegion: Region;
  @State() activeRegionIndex: number;
  @State() activeCountry: Country;
  @State() countryFilter: string;
  @State() filteredCountryList: Country[];
  @State() regionsState: Region[];
  @State() isAsyncLoad: boolean;
  @State() isVerticalScrollbar: boolean;
  @State() regionToFocusIndex: number | null = null;
  @State() shouldFocusBackButton: boolean = false;

  @Event() countrySelected: EventEmitter;
  @Event() menuOpenerClick: EventEmitter;

  private backButtonRef!: HTMLDsNavigationButtonElement;
  private regionsListRef!: HTMLElement;
  private scrollbarRef!: HTMLElement;

  @Watch('regions')
  private updateRegions() {
    if (Array.isArray(this.regions)) {
      this.regionsState = this.regions;
      this.isAsyncLoad = false;
      this.activeRegion = this.regionsState?.length == 1 ? this.regionsState[0] : null;
    } else {
      this.isAsyncLoad = true;
    }
  }

  private setActiveRegion = (region: Region, key?: number) => {
    this.countryFilter = '';
    this.activeRegion = region;
    this._dropdown?.focus();
    this.activeRegionIndex = key;
  };

  private setActiveCountry = (country: Country) => {
    this.activeCountry = country;

    if (!country.url) {
      this.closeCountrySelector();
    }

    this.countrySelected.emit(country);
  };

  componentDidUpdate(): void {
    if (this.shouldFocusBackButton) {
      this.backButtonRef.focusBtn();
      this.shouldFocusBackButton = false;
    }

    if (Number.isInteger(this.regionToFocusIndex)) {
      this.regionsListRef
        ?.querySelectorAll('ds-navigation-button')
        [this.regionToFocusIndex]?.focusBtn();
      this.regionToFocusIndex = null;
    }
  }

  private closeCountrySelector = () => {
    this.open = false;
    this.activeRegion = this.regionsState?.length == 1 ? this.regionsState[0] : null;
    this.countryFilter = '';
    this.isAsyncLoad && (this.regionsState = null);
  };

  private isSearchDisabled = () => {
    if (typeof this.searchDisabled === 'boolean') {
      return this.searchDisabled;
    }
    const maxItems = Number(this.searchDisabled);
    if (!isNaN(maxItems)) {
      return this.activeRegion.countries.length < maxItems;
    }
    return false;
  };

  private filterCountryList = (countries: Country[] = [], filter: string = '') => {
    if (!filter) {
      return countries;
    }

    return countries.filter((country) => {
      const displayTitleMatch = country.displayTitle?.toLowerCase().includes(filter.toLowerCase());
      const nativeTitleMatch = country.nativeTitle?.toLowerCase().includes(filter.toLowerCase());

      return displayTitleMatch || nativeTitleMatch;
    });
  };

  private checkDisplayVerticalScrollbar() {
    if (!this.mobile && this.scrollbarRef) {
      const verticalScroll = this.scrollbarRef?.getElementsByClassName(
        CLASS_NAME_VERTICAL_SCROLLBAR
      )?.[0];

      this.isVerticalScrollbar = !!verticalScroll?.className.includes(CLASS_NAME_VISIBLE_SCROLLBAR);
    }
  }

  componentWillLoad(): void {
    this.updateRegions();
  }

  async componentWillUpdate() {
    if (this.open && this.isAsyncLoad && !this.regionsState) {
      //@ts-ignore isAsyncLoad checks that regions is a function
      this.regionsState = await Promise.resolve(this.regions());
      this.activeRegion = this.regionsState?.length == 1 ? this.regionsState[0] : null;
    }
  }

  render() {
    if (!this.regionsState && !this.isAsyncLoad) {
      return null;
    }
    const mode = getMode(this);
    return (
      <ds-action-menu
        class={{ 'country-selector': true, 'country-selector--mobile': this.mobile }}
        open={this.open}
        mobile={this.mobile}
        openerTestId={`cxs-header-country-selector-button${this.mobile ? '-mobile' : ''}`}
        openerLabel={translate(Labels.CountrySelector)}
        dropdownTestId={`cxs-header-country-selector-menu${this.mobile ? '-mobile' : ''}`}
        onOpenerClick={(event) => {
          this.menuOpenerClick.emit();
          if (!this.mobile) {
            if (this.open) {
              this.closeCountrySelector();
            } else {
              this.open = true;

              if ((event.detail as PointerEvent).pointerType === '') {
                this.regionToFocusIndex = 0;
              }
            }

            emitClickTrackerEvent({
              componentName: ActivityMapRegion.Header,
              title: `${this.open ? 'Open' : 'Close'} Country Selector`,
              url: ''
            });
          }
        }}
        onClickOutside={() => {
          if (this.open && !this.mobile) {
            emitClickTrackerEvent({
              componentName: ActivityMapRegion.Header,
              title: 'Click Outside Country Selector',
              url: ''
            });
            this.closeCountrySelector();
          }
        }}
        data-mode={getMode(this)}
      >
        {this.defaultActiveCountry?.iconUrl ? (
          <img
            class='country-selector__opener-img'
            src={this.defaultActiveCountry.iconUrl}
            alt={translate(Labels['CountrySelector.FlagIconAltText'])}
            slot='opener-content'
          />
        ) : (
          <ds-icon
            name='Globe'
            class='country-selector__opener-icon'
            size='big'
            slot='opener-content'
          />
        )}
        <div slot='dropdown-content' class='dropdown' data-mode={getMode(this)}>
          {!this.activeRegion ? (
            <div
              class='regions'
              data-testid={`cxs-header-country-selector-regions${this.mobile ? '-mobile' : ''}`}
            >
              <span class='menu__title regions__title'>
                {translate(Labels['CountrySelector.ChooseRegion'])}
              </span>
              {this.regionsState ? (
                <div
                  class='menu__list regions__list'
                  ref={(element) => (this.regionsListRef = element as HTMLElement)}
                  role='menu'
                >
                  {this.regionsState?.map((region, key) => (
                    <li class='menu__list-item' key={key} role='none'>
                      <ds-navigation-button
                        class='menu__link regions__link'
                        color='transparent'
                        onClick={(e) => {
                          e.preventDefault();
                          this.setActiveRegion(region, key);
                          if (region) {
                            emitClickTrackerEvent({
                              componentName: ActivityMapRegion.Header,
                              title: `${this.mobile ? 'Mobile ' : ''}Region Selection: ${
                                region.title
                              }`,
                              url: ''
                            });

                            if (!e.detail && !this.mobile) {
                              this.shouldFocusBackButton = true;
                            }

                            if (!this.mobile) {
                              setTimeout(() => {
                                this.checkDisplayVerticalScrollbar();
                              }, 50);
                            }
                          }
                        }}
                        elementRole='menuitem'
                      >
                        <div class='link'>
                          {region.title}
                          <ds-icon aria-hidden='true' name='NavigationArrowRight' />
                        </div>
                      </ds-navigation-button>
                    </li>
                  ))}
                </div>
              ) : null}
              {this.lastVisitedCountry ? (
                <div class='last-visited'>
                  <span class='last-visited__title'>
                    <span class='last-visited__prefix'>
                      {translate(Labels['CountrySelector.LastVisited'])}:
                    </span>
                    <ds-link
                      class='menu__link countries__link'
                      type='primary'
                      onClick={() => {
                        this.setActiveCountry(this.lastVisitedCountry);
                        emitClickTrackerEvent({
                          componentName: ActivityMapRegion.Header,
                          title: `${this.mobile ? 'Mobile ' : ''}Country Selection: ${
                            this.lastVisitedCountry.displayTitle
                          }`,
                          url: this.lastVisitedCountry.url || ''
                        });
                      }}
                      href={this.lastVisitedCountry.url || '#'}
                    >
                      {this.lastVisitedCountry.displayTitle}
                    </ds-link>
                  </span>
                </div>
              ) : null}
            </div>
          ) : (
            <Fragment>
              {this.regionsState?.length > 1 ? (
                <span
                  class={{
                    menu__title: true,
                    countries__title: true,
                    'countries__title--with-search': true
                  }}
                >
                  {translate(Labels['CountrySelector.ChooseCountry'])}
                </span>
              ) : (
                <span
                  class={{
                    menu__title: true,
                    countries__title: true,
                    'countries__title--with-search': !this.isSearchDisabled()
                  }}
                >
                  {translate(Labels['CountrySelector.ChooseLanguage'])}
                </span>
              )}
              <ul
                class='countries'
                data-testid={`cxs-header-country-selector-countries${this.mobile ? '-mobile' : ''}`}
              >
                <div class='countries__top-section' role='none'>
                  {this.regionsState?.length > 1 && (
                    <li
                      class='countries__back-to-region-title'
                      role='none'
                      key={`back-to-${this.activeRegion.title}`}
                    >
                      <ds-navigation-button
                        class='countries__back-to-region-button'
                        label={translate(Labels['CountrySelector.BackToRegions'])}
                        onClick={(e) => {
                          if (!e.detail && !this.mobile) {
                            this.regionToFocusIndex = this.activeRegionIndex;
                          }
                          this.setActiveRegion(null);
                        }}
                        color='transparent'
                        ref={(element) => (this.backButtonRef = element)}
                        elementRole='listitem'
                      >
                        <div class='button-content-wrapper'>
                          <ds-icon
                            name={mode === 'light' ? 'NavigationArrowLeft' : 'ArrowLeft'}
                            class='country-selector__return-button'
                          />
                          <span class='active-region-title'>{this.activeRegion.title}</span>
                        </div>
                      </ds-navigation-button>
                    </li>
                  )}
                  {!this.isSearchDisabled() && (
                    <li>
                      <ds-search-input
                        class='countries__search'
                        hideSubmitButton
                        onSearchQueryChanged={(event) => (this.countryFilter = event.detail)}
                        type='menu'
                        autoFocus={this.mobile}
                      />
                    </li>
                  )}
                </div>
                <div
                  role='none'
                  class={{
                    menu__list: true,
                    countries__list: true,
                    'countries__list--with-scrollbar': this.isVerticalScrollbar
                  }}
                >
                  <ds-custom-scrollbar
                    class={{ countries__list__scrollbar: !this.mobile }}
                    ref={(element) => (this.scrollbarRef = element as HTMLElement)}
                  >
                    {this.filterCountryList(this.activeRegion.countries, this.countryFilter).map(
                      (country, key) => (
                        <li class='menu__list-item' key={key}>
                          <ds-link
                            class={{
                              menu__link: true,
                              countries__link: true,
                              active: this.defaultActiveCountry?.countryCode === country.countryCode
                            }}
                            type='secondary'
                            onClick={() => {
                              this.setActiveCountry(country);
                              emitClickTrackerEvent({
                                componentName: ActivityMapRegion.Header,
                                title: `${this.mobile ? 'Mobile ' : ''}Country Selection: ${
                                  country.displayTitle
                                }`,
                                url: country.url || ''
                              });
                            }}
                            href={country.url || '#'}
                          >
                            {country.displayTitle}
                          </ds-link>
                        </li>
                      )
                    )}
                  </ds-custom-scrollbar>
                </div>
              </ul>
            </Fragment>
          )}
        </div>
      </ds-action-menu>
    );
  }
}
